import { z } from "zod";
import { parseDay } from "./schedule";

export const REJECTION_REASON_MIN = 10;

const day = z.string().refine((v) => parseDay(v) !== null, "Choose a date.");

export const createProjectSchema = z.object({
  clientId: z.number().int().positive("Choose a client."),
  name: z.string().trim().min(1, "Give the project a name.").max(255),
  packageName: z.string().trim().max(255).default(""),
  budgetHours: z.number().min(0, "Enter an hour budget above zero.").max(100_000).multipleOf(0.25, "Use steps of 15 minutes (0.25 h)."),
  budgetChf: z.number().min(0).max(100_000_000).nullable(),
  /** Hourly rate for this project when it differs from the default (people with pm:money_view only). */
  hourlyRate: z.number().min(0).max(10_000).nullable().default(null),
  workPackageId: z.number().int().positive().nullable().default(null),
  templateId: z.number().int().positive().nullable(),
  startDate: z.string().refine((v) => parseDay(v) !== null, "Choose a start date."),
  goLiveDate: z.string().refine((v) => parseDay(v) !== null, "Choose a go-live date.").nullable(),
  bucketDates: z
    .array(z.object({ index: z.number().int().min(0).max(100), start: day, end: day }).refine((d) => d.end >= d.start, "A bucket cannot end before it starts."))
    .max(100)
    .default([]),
  roles: z
    .array(
      z.object({
        roleLabelId: z.number().int().positive(),
        userIds: z.array(z.number().int().positive()).max(30),
        customers: z.array(z.object({ name: z.string().trim().min(1).max(255), email: z.string().trim().max(255).email("Enter a valid email or leave it empty.").or(z.literal("")) })).max(30),
      }),
    )
    .max(40),
}).superRefine((v, ctx) => {
  const start = parseDay(v.startDate);
  const end = v.goLiveDate ? parseDay(v.goLiveDate) : null;
  if (v.templateId && !v.goLiveDate) ctx.addIssue({ code: "custom", path: ["goLiveDate"], message: "Choose a go-live date." });
  if (start && end && end < start) ctx.addIssue({ code: "custom", path: ["goLiveDate"], message: "The go-live date cannot be before the start date." });
  if (v.workPackageId) {
    // budgetHours may be 0, packageName is ignored
  } else {
    if (!v.packageName || v.packageName.trim().length === 0) {
      ctx.addIssue({ code: "custom", path: ["packageName"], message: "Give the budget a name." });
    }
    if (v.budgetHours <= 0) {
      ctx.addIssue({ code: "custom", path: ["budgetHours"], message: "Enter an hour budget above zero." });
    }
  }
});

export const approveSchema = z.object({ id: z.number().int().positive() });
export const rejectSchema = z.object({
  id: z.number().int().positive(),
  reason: z.string().trim().min(REJECTION_REASON_MIN, `Write at least ${REJECTION_REASON_MIN} characters so the manager knows what to change.`).max(2000),
});

export type CreateProjectInput = z.infer<typeof createProjectSchema>;
